import { Injectable, InjectionToken, NgZone, inject } from '@angular/core';
import { environment } from '@env/environment';
import { AnalyticsRouteDecision, ANALYTICS_OFF } from './analytics-route-policy';

type AnalyticsSdk = typeof import('@amplitude/analytics-browser');
type ReplayPlugin = ReturnType<typeof import('@amplitude/plugin-session-replay-browser').sessionReplayPlugin>;

export interface AmplitudeSettings {
  enabled: boolean;
  apiKey: string;
  replayEnabled: boolean;
  replaySampleRate: number;
}

export const AMPLITUDE_SETTINGS = new InjectionToken<AmplitudeSettings>('Amplitude settings', {
  providedIn: 'root',
  factory: () => environment.amplitude
});

export const AMPLITUDE_SDK_LOADER = new InjectionToken<() => Promise<AnalyticsSdk>>('Amplitude SDK loader', {
  providedIn: 'root',
  factory: () => () => import('@amplitude/analytics-browser')
});

export const AMPLITUDE_REPLAY_LOADER = new InjectionToken<() => Promise<typeof import('@amplitude/plugin-session-replay-browser')>>('Amplitude Replay loader', {
  providedIn: 'root',
  factory: () => () => import('@amplitude/plugin-session-replay-browser')
});

export type AnalyticsEventName =
  | 'Page Viewed'
  | 'Registration Started'
  | 'PMS Provider Selected'
  | 'PMS Connection Attempted'
  | 'PMS Connection Succeeded'
  | 'PMS Connection Failed'
  | 'PMS Connection Result'
  | 'Property Selection Completed'
  | 'Account Details Submitted'
  | 'Account Details Succeeded'
  | 'Account Details Failed'
  | 'Billing Plan Selected'
  | 'Checkout Setup Started'
  | 'Checkout Started'
  | 'Checkout Setup Failed'
  | 'Checkout Failed'
  | 'Checkout Returned'
  | 'Checkout Canceled'
  | 'Checkout Confirmed'
  | 'Owner Account Created'
  | 'Registration Step Reached'
  | 'Subscription Setup Result'
  | 'Owner Feature Action'
  | 'Owner Feature Opened'
  | 'Owner Filter Changed'
  | 'Owner Workflow Started'
  | 'Owner Action Completed'
  | 'Owner Export Initiated'
  | 'Owner Action Failed'
  | 'Owner Import Processing Result';

// Only controlled vocabulary is accepted; no free text or raw URL properties.
export interface SafeAnalyticsProperties {
  feature?: 'dashboard' | 'action_queue' | 'properties' | 'imports' | 'addons' | 'signals' | 'certifications' | 'settings' | 'help';
  action?: 'opened' | 'started' | 'completed' | 'approved' | 'declined' | 'generated' | 'changed' | 'activated' | 'canceled' | 'certified' | 'saved' | 'sent' | 'tested' | 'snoozed';
  workflow?: 'action_review' | 'property_create' | 'property_edit' | 'property_sync' | 'import_upload' | 'import_processing' | 'addon_setup' | 'participation_report' | 'profile_settings' | 'notification_settings' | 'pms_connection' | 'help_tour' | 'faq';
  filter?: 'queue' | 'property_sort' | 'help_category' | 'certification_tab';
  step?: 'connect' | 'select_properties' | 'account_details' | 'billing' | 'checkout_return';
  result?: 'success' | 'failure' | 'canceled';
  provider?: 'ownerrez' | 'beds24' | 'other';
  error_category?: 'validation' | 'authorization' | 'connection' | 'payment' | 'network' | 'unknown';
  property_count_bucket?: '1' | '2-5' | '6-20' | '21+';
  billing_interval?: 'monthly' | 'annual';
  import_stage?: 'ready_for_approval' | 'complete' | 'unable_to_process';
}

const SAFE_VALUES: { [K in keyof SafeAnalyticsProperties]: readonly NonNullable<SafeAnalyticsProperties[K]>[] } = {
  feature: ['dashboard', 'action_queue', 'properties', 'imports', 'addons', 'signals', 'certifications', 'settings', 'help'],
  action: ['opened', 'started', 'completed', 'approved', 'declined', 'generated', 'changed', 'activated', 'canceled', 'certified', 'saved', 'sent', 'tested', 'snoozed'],
  workflow: ['action_review', 'property_create', 'property_edit', 'property_sync', 'import_upload', 'import_processing', 'addon_setup', 'participation_report', 'profile_settings', 'notification_settings', 'pms_connection', 'help_tour', 'faq'],
  filter: ['queue', 'property_sort', 'help_category', 'certification_tab'],
  step: ['connect', 'select_properties', 'account_details', 'billing', 'checkout_return'],
  result: ['success', 'failure', 'canceled'],
  provider: ['ownerrez', 'beds24', 'other'],
  error_category: ['validation', 'authorization', 'connection', 'payment', 'network', 'unknown'],
  property_count_bucket: ['1', '2-5', '6-20', '21+'],
  billing_interval: ['monthly', 'annual'],
  import_stage: ['ready_for_approval', 'complete', 'unable_to_process']
};

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private readonly settings = inject(AMPLITUDE_SETTINGS);
  private readonly loadSdk = inject(AMPLITUDE_SDK_LOADER);
  private readonly loadReplay = inject(AMPLITUDE_REPLAY_LOADER);
  private readonly zone = inject(NgZone);

  private sdk: AnalyticsSdk | null = null;
  private initialization: Promise<AnalyticsSdk | null> | null = null;
  private replayPlugin: ReplayPlugin | null = null;
  private replayAttached = false;
  private replayOperation: Promise<void> = Promise.resolve();
  private decision: AnalyticsRouteDecision = { area: null, routeTemplate: null, policy: ANALYTICS_OFF };
  private userId: string | null = null;
  private routeVersion = 0;
  private replayViewRequested = false;

  get enabled(): boolean {
    return this.settings.enabled && this.settings.apiKey.trim().length > 0;
  }

  get currentOwnerRouteTemplate(): string | null {
    return this.decision.area === 'owner' && this.decision.policy.customEvents
      ? this.decision.routeTemplate : null;
  }

  /** Stops SDK event delivery immediately; Replay teardown is queued before re-enabling. */
  suspend(): Promise<void> {
    this.routeVersion++;
    this.replayViewRequested = false;
    this.decision = { area: null, routeTemplate: null, policy: ANALYTICS_OFF };
    if (this.sdk) this.zone.runOutsideAngular(() => this.sdk!.setOptOut(true));
    return this.setReplayAllowed(false).catch(() => {});
  }

  async setRoute(decision: AnalyticsRouteDecision): Promise<boolean> {
    const version = ++this.routeVersion;
    this.decision = decision;
    const allowed = decision.policy.pageView || decision.policy.customEvents || decision.policy.replay;
    if (!this.enabled || !allowed) {
      if (this.sdk) this.zone.runOutsideAngular(() => this.sdk!.setOptOut(true));
      await this.setReplayAllowed(false).catch(() => {});
      return version === this.routeVersion;
    }

    const sdk = await this.ensureSdk();
    if (!sdk || version !== this.routeVersion) return false;
    this.zone.runOutsideAngular(() => {
      sdk.setOptOut(false);
      if (this.userId) sdk.setUserId(this.userId);
    });
    await this.setReplayAllowed(decision.policy.replay && this.replayViewRequested).catch(() => {});
    return version === this.routeVersion;
  }

  track(eventName: AnalyticsEventName, safeProperties: SafeAnalyticsProperties = {}): boolean {
    const isPageView = eventName === 'Page Viewed';
    if (!this.enabled || !this.sdk || !this.decision.routeTemplate || !this.decision.area ||
      !(isPageView ? this.decision.policy.pageView : this.decision.policy.customEvents)) return false;

    const properties: Record<string, string> = {
      ApplicationName: 'ActionLedgerOwner',
      Env: environment.name,
      route_template: this.decision.routeTemplate,
      area: this.decision.area
    };
    for (const key of Object.keys(SAFE_VALUES) as (keyof SafeAnalyticsProperties)[]) {
      const value = safeProperties[key];
      if (typeof value === 'string' && (SAFE_VALUES[key] as readonly string[]).includes(value)) {
        properties[key] = value;
      }
    }
    this.zone.runOutsideAngular(() => this.sdk!.track(eventName, properties));
    return true;
  }

  /** The route permits Replay, but the view must also opt in while its DOM is safe. */
  setReplayViewAllowed(allowed: boolean): Promise<void> {
    this.replayViewRequested = allowed;
    return this.setReplayAllowed(allowed);
  }

  /** Cache identity until an allowed route is active; never identify on excluded routes. */
  identify(internalUserId: string | null): void {
    this.userId = internalUserId?.trim() || null;
    if (this.sdk && this.decision.routeTemplate && this.userId) {
      this.zone.runOutsideAngular(() => this.sdk!.setUserId(this.userId!));
    }
  }

  resetIdentity(): void {
    this.userId = null;
    if (this.sdk) this.zone.runOutsideAngular(() => this.sdk!.reset());
  }

  /** Serializes add/remove so a late add cannot leave Replay active after suspension. */
  setReplayAllowed(allowed: boolean): Promise<void> {
    const requestedVersion = this.routeVersion;
    const shouldRecord = allowed && this.enabled && this.settings.replayEnabled &&
      this.settings.replaySampleRate > 0 && this.decision.policy.replay;
    this.replayOperation = this.replayOperation.catch(() => {}).then(async () => {
      if (!shouldRecord || requestedVersion !== this.routeVersion) {
        if (this.sdk && this.replayPlugin && this.replayAttached) {
          await this.zone.runOutsideAngular(() => this.sdk!.remove(this.replayPlugin!.name).promise);
          this.replayAttached = false;
        }
        return;
      }
      const sdk = await this.ensureSdk();
      if (!sdk || requestedVersion !== this.routeVersion || this.replayAttached) return;
      if (!this.replayPlugin) {
        const replay = await this.loadReplay();
        if (requestedVersion !== this.routeVersion) return;
        this.replayPlugin = replay.sessionReplayPlugin({
          sampleRate: Math.min(1, Math.max(0, this.settings.replaySampleRate)),
          // Replay otherwise records the full page URL, including arbitrary query values.
          interactionConfig: {
            enabled: true,
            batch: false,
            ugcFilterRules: typeof window === 'undefined' ? [] : [{
              selector: `${window.location.origin}/*`,
              replacement: `${window.location.origin}/register`
            }]
          },
          privacyConfig: {
            defaultMaskLevel: 'conservative',
            blockSelector: ['.amp-block', '[data-analytics-private]']
          }
        });
      }
      await this.zone.runOutsideAngular(() => sdk.add(this.replayPlugin!).promise);
      this.replayAttached = true;
      if (requestedVersion !== this.routeVersion) {
        await this.zone.runOutsideAngular(() => sdk.remove(this.replayPlugin!.name).promise);
        this.replayAttached = false;
      }
    });
    return this.replayOperation;
  }

  private ensureSdk(): Promise<AnalyticsSdk | null> {
    if (!this.enabled) return Promise.resolve(null);
    if (this.sdk) return Promise.resolve(this.sdk);
    if (!this.initialization) {
      this.initialization = this.loadSdk().then(async (sdk) => {
        const host = typeof window === 'undefined' ? '' : window.location.hostname.toLowerCase();
        const sharedDomain = host === 'actionledgers.com' || host.endsWith('.actionledgers.com');
        await this.zone.runOutsideAngular(() => sdk.init(this.settings.apiKey.trim(), undefined, {
          autocapture: false,
          optOut: true,
          ...(sharedDomain ? { cookieOptions: { domain: '.actionledgers.com', secure: true } } : {})
        }).promise);
        this.sdk = sdk;
        return sdk;
      }).catch(() => null);
    }
    return this.initialization;
  }
}
